// Lib
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Editor } from '@tiptap/react';
import { type Range } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
import { isEmpty } from 'lodash';

// Utils
import { getExtendedMarksBetween } from '../../../../../../common/tiptap/utils/getExtendedMarksBetween';
import { getCollapsedCommentPopupId } from '../../../../../../common/elements/utils/collapsedCommentPopupUtils';
import { getNewTransactionId } from '../../../../../utils/undoRedo/undoRedoTransactionManager';

// Actions
import { createElement, getNewElementId } from '../../../../../element/actions/elementActions';
import { togglePopup } from '../../../../../components/popupPanel/popupActions';
import { setSelectedElements } from '../../../../../element/selection/selectionActions';

// Hooks
import {
    useActiveTiptapEditorCallback,
    useTiptapActiveEditorSelector,
} from '../../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Selectors
import {
    selectIsMarkWithinSelection,
    selectIsNodeWithinSelection,
    selectIsSelectionRangeActive,
} from './selectors/tiptapToolSelectors';

// Types
import { ElementType } from '../../../../../../common/elements/elementTypes';
import { BoardSections } from '../../../../../../common/boards/boardConstants';
import { AttachMode } from '../../../../../../common/elements/elementModelTypes';
import {
    NoteInsertionSource,
    NoteInsertionType,
    trackNoteInsertionEvent,
} from '../../../../../analytics/noteInsertionAnalyticsUtils';

/**
 * Finds the comment threads whose inline comments the editor's current selection touches.
 */
const getSelectedCommentThreadIds = (activeEditor: Editor): string[] => {
    const { from, to } = activeEditor.state.selection;
    const markType = activeEditor.state.schema.marks.inlineComment;

    return getExtendedMarksBetween(from, to, activeEditor.state.doc, [markType]).map(
        ({ mark }) => mark.attrs.commentThreadId as string,
    );
};

type UseInlineCommentToolArgs = {
    // The element that owns the editor - the comment thread is attached inline to it
    parentElementId: string;
    // Resolves the range to comment when there is no text selection. Without it, a collapsed
    // selection has nothing to comment and the tool reports itself as uncommentable.
    getCollapsedSelectionRange?: (state: EditorState) => Range | undefined;
};

type InlineCommentToolState = {
    // Whether the selection already contains an inline comment
    isActive: boolean;
    // Whether there is text the tool could comment on
    canAddComment: boolean;
    toggleInlineComment: (event: PointerEvent) => void;
};

/**
 * Drives an inline comment toolbar tool: reports the state its button needs, and toggles the
 * inline comment on the active editor.
 *
 * Toggling opens the existing thread when the selection already has an inline comment, and
 * otherwise marks the range being commented and creates the comment thread element behind it.
 */
const useInlineCommentTool = ({
    parentElementId,
    getCollapsedSelectionRange,
}: UseInlineCommentToolArgs): InlineCommentToolState => {
    const isCodeActive = useTiptapActiveEditorSelector(selectIsMarkWithinSelection('code'));
    const isCodeBlockActive = useTiptapActiveEditorSelector(selectIsNodeWithinSelection('codeBlock'));
    const isActive = useTiptapActiveEditorSelector(selectIsMarkWithinSelection('inlineComment'));
    const hasSelectionRange = useTiptapActiveEditorSelector(selectIsSelectionRangeActive);
    const hasCollapsedSelectionRange = useTiptapActiveEditorSelector(
        (editor) => !!editor && !!getCollapsedSelectionRange?.(editor.state),
    );

    const dispatch = useDispatch();

    /**
     * Opens the attached comment popup associated with this inline comment.
     */
    const openInlineCommentPopup = useCallback((commentThreadId: string) => {
        dispatch(setSelectedElements({ ids: [commentThreadId], transactionId: undefined, rangeAnchors: undefined }));
        // Opens the inline comment popup for the newly created comment thread
        dispatch(
            togglePopup({
                popupId: getCollapsedCommentPopupId(commentThreadId),
            }),
        );
    }, []);

    /**
     * Adds a new inline comment to the current selection in the editor.
     */
    const addInlineComment = useCallback(
        (activeEditor: Editor, source: NoteInsertionSource) => {
            const commentThreadId = dispatch(getNewElementId()) as unknown as string;
            const transactionId = getNewTransactionId();

            // Adds the inline comment mark to the current selection
            activeEditor
                .chain()
                .toggleMark('inlineComment', { commentThreadId })
                .flushPendingUpdate({ transactionId })
                .run();

            // Creates a new comment thread element to back the inline comment
            dispatch(
                createElement(
                    {
                        elementType: ElementType.COMMENT_THREAD_TYPE,
                        location: {
                            parentId: parentElementId,
                            section: BoardSections.ATTACHED,
                            position: {
                                attachMode: AttachMode.inline,
                            },
                        },
                        transactionId,
                    },
                    commentThreadId,
                ),
            );

            trackNoteInsertionEvent(NoteInsertionType.COMMENT, source);
            openInlineCommentPopup(commentThreadId);
        },
        [parentElementId],
    );

    /**
     * Opens the inline comment the selection already touches, or comments the selection.
     *
     * For a bare caret the selection is first expanded to the range this tool comments. The marks
     * are then re-read from that expanded range rather than trusting `isActive`, which was computed
     * before the expansion: toggling a range that reaches an existing inline comment would strip the
     * mark and trash its thread, so an existing comment is opened instead.
     */
    const toggleInlineComment = useActiveTiptapEditorCallback(
        (activeEditor, event: PointerEvent) => {
            if (!activeEditor) return;

            event.preventDefault();

            if (!isActive && activeEditor.state.selection.empty) {
                const range = getCollapsedSelectionRange?.(activeEditor.state);

                if (!range) return;

                activeEditor.commands.setTextSelection(range);
            }

            const selectedCommentThreadIds = getSelectedCommentThreadIds(activeEditor);

            if (!isEmpty(selectedCommentThreadIds)) return openInlineCommentPopup(selectedCommentThreadIds[0]);

            addInlineComment(activeEditor, NoteInsertionSource.TOOLBAR);
        },
        [isActive, addInlineComment, getCollapsedSelectionRange, openInlineCommentPopup],
    );

    return {
        isActive,
        // Code marks and code blocks can't carry an inline comment
        canAddComment: !isCodeActive && !isCodeBlockActive && (hasSelectionRange || hasCollapsedSelectionRange),
        toggleInlineComment,
    };
};

export default useInlineCommentTool;
